import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
    LayoutDashboard,
    Users,
    Wallet,
    ClipboardCheck,
    Settings as SettingsIcon,
    LogOut,
    PiggyBank,
    TrendingUp,
    BarChart3,
    History,
    MoreHorizontal,
    Lock,
    MessageCircle,
    Mail,
    ClipboardList,
    CalendarDays,
    Archive,
    Coins,
    ShieldCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { usePin } from "../context/PinContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import EmployeeWelcomePopup from "./EmployeeWelcomePopup";
import { BrandMark } from "./BrandMark";

function SidebarItem({ to, icon: Icon, label, testid, onClick }) {
    return (
        <NavLink
            to={to}
            data-testid={testid}
            end={to === "/"}
            onClick={onClick}
            className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl transition tap ${
                    isActive ? "bg-blue-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                }`
            }
        >
            <Icon className="h-5 w-5" strokeWidth={1.75} />
            <span className="font-medium text-sm">{label}</span>
        </NavLink>
    );
}

function BottomItem({ to, icon: Icon, label, testid }) {
    return (
        <NavLink
            to={to}
            data-testid={testid}
            end={to === "/"}
            className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 flex-1 ${
                    isActive ? "text-blue-900" : "text-slate-500"
                }`
            }
        >
            <Icon className="h-5 w-5" strokeWidth={1.75} />
            <span className="text-[10px] font-medium">{label}</span>
        </NavLink>
    );
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { t } = useLang();
    const pin = usePin();
    const nav = useNavigate();
    const [moreOpen, setMoreOpen] = useState(false);

    const isAdmin = user?.role === "admin" || user?.role === "super_admin";
    const isSuperAdmin = user?.role === "super_admin";

    // ── Bottom-nav primary tabs ──
    const primary = isAdmin
        ? [
              { to: "/", icon: LayoutDashboard, label: t("dashboard"), testid: "nav-dashboard" },
              { to: "/customers", icon: Users, label: t("customers"), testid: "nav-customers" },
              { to: "/loans", icon: Wallet, label: t("loans"), testid: "nav-loans" },
              { to: "/pigmy", icon: PiggyBank, label: t("pigmy"), testid: "nav-pigmy" },
          ]
        : [
              { to: "/", icon: ClipboardList, label: "My Sheet", testid: "nav-dashboard" },
              { to: "/customers", icon: Users, label: t("customers"), testid: "nav-customers" },
              { to: "/pigmy", icon: PiggyBank, label: t("pigmy"), testid: "nav-pigmy" },
              { to: "/reminders", icon: MessageCircle, label: "Reminders", testid: "nav-reminders" },
          ];

    // ── Secondary (More sheet on mobile, lower section on desktop sidebar) ──
    const secondary = isAdmin
        ? [
              { to: "/collections", icon: ClipboardCheck, label: t("approvals"), testid: "nav-collections" },
              { to: "/reminders", icon: MessageCircle, label: "Reminders", testid: "nav-reminders" },
              { to: "/daily-sheet", icon: ClipboardList, label: "Daily Sheet", testid: "nav-daily-sheet" },
              { to: "/day-close", icon: Coins, label: "Day Close", testid: "nav-day-close" },
              { to: "/calendar", icon: CalendarDays, label: "EMI Calendar", testid: "nav-calendar" },
              { to: "/investors", icon: TrendingUp, label: "Investors", testid: "nav-investors" },
              { to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports" },
              { to: "/audit-log", icon: History, label: "Audit Log", testid: "nav-audit" },
              { to: "/enquiries", icon: Mail, label: "Enquiries", testid: "nav-enquiries" },
              { to: "/archive", icon: Archive, label: "Archive", testid: "nav-archive" },
              ...(isSuperAdmin ? [{ to: "/super-admin", icon: ShieldCheck, label: "Super Admin", testid: "nav-super-admin" }] : []),
              { to: "/settings", icon: SettingsIcon, label: t("settings"), testid: "nav-settings" },
          ]
        : [
              { to: "/collections", icon: ClipboardCheck, label: t("collections"), testid: "nav-collections" },
              { to: "/day-close", icon: Coins, label: "Day Close", testid: "nav-day-close" },
              { to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports" },
              { to: "/settings", icon: SettingsIcon, label: t("settings"), testid: "nav-settings" },
          ];
    const allSidebar = [...primary, ...secondary];

    async function doLogout() {
        await logout();
        nav("/login");
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {!isAdmin && <EmployeeWelcomePopup />}

            {/* Top horizontal header bar removed by design — vertical real-estate
                is reclaimed for the dashboard. Brand + nav live in the sidebar
                (desktop) and the bottom-nav More sheet (mobile). */}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
                {/* Desktop sidebar — full-height, brand at top, login/logout at bottom */}
                <aside className="hidden lg:flex flex-col gap-3 sticky top-4 h-[calc(100vh-2rem)]">
                    <Link
                        to="/"
                        className="flex items-center justify-center py-3"
                        data-testid="brand-link"
                    >
                        <BrandMark size={56} />
                    </Link>

                    <nav className="bg-white rounded-2xl border border-slate-100 p-2.5 space-y-0.5 flex-1 overflow-y-auto">
                        {allSidebar.map((i) => (
                            <SidebarItem key={i.to} {...i} />
                        ))}
                    </nav>

                    <div className="bg-white rounded-2xl border border-slate-100 p-2.5 space-y-0.5">
                        {pin.enabled && (
                            <button
                                data-testid="lock-app-btn-sidebar"
                                onClick={pin.lockNow}
                                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition tap"
                            >
                                <Lock className="h-5 w-5" strokeWidth={1.75} />
                                <span className="font-medium text-sm">Lock app</span>
                            </button>
                        )}
                        <div className="px-4 py-1.5">
                            <div className="text-xs font-semibold text-slate-700">{user?.name}</div>
                            <div className="text-[10px] text-slate-500 capitalize">{user?.role}</div>
                        </div>
                        <button
                            data-testid="logout-btn-sidebar"
                            onClick={doLogout}
                            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-rose-700 hover:bg-rose-50 transition tap"
                        >
                            <LogOut className="h-5 w-5" strokeWidth={1.75} />
                            <span className="font-medium text-sm">{t("logout")}</span>
                        </button>
                    </div>
                </aside>

                <main className="pb-24 lg:pb-6">{children}</main>
            </div>

            {/* Bottom nav (mobile only) — primary tabs + More sheet */}
            <nav className="bottom-nav lg:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-100 z-40">
                <div className="flex items-stretch">
                    {primary.map((i) => (
                        <BottomItem key={i.to} {...i} />
                    ))}
                    <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="nav-more"
                                className="flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 flex-1 text-slate-500"
                            >
                                <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} />
                                <span className="text-[10px] font-medium">More</span>
                            </button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="rounded-t-2xl">
                            <SheetHeader>
                                <SheetTitle className="flex items-center justify-center py-1">
                                    <BrandMark size={40} />
                                </SheetTitle>
                            </SheetHeader>
                            <div className="mt-3 space-y-1">
                                {secondary.map((i) => (
                                    <SidebarItem
                                        key={i.to}
                                        {...i}
                                        onClick={() => setMoreOpen(false)}
                                    />
                                ))}

                                {/* Lock app (if PIN enabled) */}
                                {pin.enabled && (
                                    <button
                                        data-testid="lock-app-btn"
                                        onClick={() => {
                                            setMoreOpen(false);
                                            pin.lockNow();
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition tap"
                                    >
                                        <Lock className="h-5 w-5" strokeWidth={1.75} />
                                        <span className="font-medium text-sm">Lock app</span>
                                    </button>
                                )}

                                {/* User identity + Logout — placed at the very bottom */}
                                <div className="mt-2 pt-3 border-t border-slate-100">
                                    <div className="px-4 py-2">
                                        <div className="text-sm font-semibold text-slate-700">
                                            {user?.name}
                                        </div>
                                        <div className="text-xs text-slate-500 capitalize">
                                            {user?.role}
                                        </div>
                                    </div>
                                    <button
                                        data-testid="logout-btn"
                                        onClick={async () => {
                                            setMoreOpen(false);
                                            await doLogout();
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-rose-700 hover:bg-rose-50 transition tap"
                                    >
                                        <LogOut className="h-5 w-5" strokeWidth={1.75} />
                                        <span className="font-medium text-sm">{t("logout")}</span>
                                    </button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </nav>
        </div>
    );
}
